"use client";

import Image from "next/image";
import React from "react";
import type { Locale } from "@/cms/blocks/registry/types";
import { CaptionSVG } from "@/components/ui/SVG";

type Props = {
  data: any;
  locale: Locale;
  className?: string;
  showCaption?: boolean;
};

export function ImageBlock({
  data,
  locale,
  className,
  showCaption = true,
}: Props) {
  const [src, setSrc] = React.useState<string | null>(null);
  const [alt, setAlt] = React.useState<string | undefined>();
  const [dimensions, setDimensions] = React.useState<{
    w?: number; h?: number
  }>({});
  const caption =
    typeof data?.alt === "string" && data.alt.trim().length > 0
      ? data.alt.trim()
      : null;

  React.useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const response = await fetch("/api/cms/resolve-media", {
          method: "POST",
          headers: { "content-type": "application/json" },
          body: JSON.stringify({ ref: data, locale }),
        });
        const payload = await response.json();
        if (!cancelled && payload?.src) {
          setSrc(payload.src);
          setAlt(payload.alt);
          setDimensions({ w: payload.width, h: payload.height });
        }
      } catch (err) {
        console.warn("resolve-media failed", err);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [data, locale]);

  if (!src) return <div className="bg-gray-100 h-40 rounded" />;
  const width = dimensions.w ?? 800;
  const height = dimensions.h ?? 450;
  const imageClassName = className ?? "rounded-lg object-contain";
  return (
    <div className={`flex flex-col gap-2 ${data.variant === "hero" ? "" : "pt-2.5 pb-5.5"}`}>
      <Image
        src={src}
        alt={alt || ""}
        width={width}
        height={height}
        className={imageClassName}
        unoptimized
      />
      {showCaption && caption && (<span className={`relative text-xs lg:text-sm pr-8 mx-3 text-gray-500 leading-5`} dir={locale === "fa" ? "rtl" : "ltr"}>{caption}
        <CaptionSVG className="w-7 h-7 absolute -top-1.5 right-0" accentColor="var(--color-gray-500)" svgColor="pinkCustom" />
      </span>)}
    </div>
  );
}
